import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetModalProps,
  BottomSheetScrollView,
  BottomSheetView,
  useBottomSheetTimingConfigs,
} from '@gorhom/bottom-sheet';
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { BackHandler, StyleSheet, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SMALL_SCREEN_MAX_WIDTH } from 'shared/constants/layout';
import { isIOS, isWeb } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';

export type Ref = BottomSheetModal;

export interface BottomSheetProps extends BottomSheetModalProps {
  children: React.ReactNode;
  contentContainerStyle?: ViewStyle;
  showOverlay?: boolean; // Prop to control overlay display
  allowScrolling?: boolean;
  analyticsId?: string;
  customAttributes?: Record<string, string>;
  onDismiss?: () => void;
  setIsBottomSheetOpened?: Dispatch<SetStateAction<boolean>>;
}

const BottomSheet = forwardRef<Ref, BottomSheetProps>(
  (
    {
      children,
      contentContainerStyle,
      showOverlay = true,
      allowScrolling = false,
      analyticsId = '',
      customAttributes,
      onDismiss,
      setIsBottomSheetOpened,
      ...restProps
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const animationConfigs = useBottomSheetTimingConfigs({
      duration: 250,
    });

    const { bottom, top } = useSafeAreaInsets();

    useViewDidFocusScreenAnalyticsEvent(
      isVisible && analyticsId
        ? {
            key: analyticsId,
            customAttributes,
          }
        : null,
    );

    useEffect(() => {
      const handleBackPress = () => {
        if (
          isVisible &&
          ref &&
          (ref as React.MutableRefObject<BottomSheetModal>).current
        ) {
          onDismiss?.();
          (ref as React.MutableRefObject<BottomSheetModal>).current?.dismiss();
          return true;
        }
        return false;
      };

      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => backHandler.remove();
    }, [isVisible, onDismiss, ref]);

    const handleOnChange = useCallback(
      (index: number) => {
        setIsVisible(index >= 0);
        setIsBottomSheetOpened?.(index >= 0);
      },
      [setIsBottomSheetOpened],
    );

    const renderBackdrop = useCallback(
      (props) =>
        showOverlay ? (
          <BottomSheetBackdrop
            {...props}
            disappearsOnIndex={-1}
            appearsOnIndex={0}
            onPress={onDismiss}
          />
        ) : null,
      [onDismiss, showOverlay],
    );

    return (
      <BottomSheetModal
        ref={ref}
        index={0}
        enableDynamicSizing
        handleIndicatorStyle={styles.indicatorStyle}
        backdropComponent={renderBackdrop}
        backgroundStyle={styles.backgroundStyle}
        animationConfigs={animationConfigs}
        handleStyle={styles.handleStyle}
        containerStyle={isWeb ? styles.webContainer : undefined}
        onChange={handleOnChange}
        onDismiss={onDismiss}
        accessible={false}
        topInset={top + theme.spacing.m}
        style={styles.modalStyle}
        {...restProps}
      >
        {allowScrolling ? (
          <BottomSheetScrollView
            testID="bottom-sheet-scroll-view"
            accessible={false}
            contentContainerStyle={StyleSheet.flatten([
              styles.contentContainer,
              { paddingBottom: bottom },
              contentContainerStyle,
            ])}
          >
            {children}
          </BottomSheetScrollView>
        ) : (
          <BottomSheetView
            testID="bottom-sheet-view"
            accessible={false}
            style={StyleSheet.flatten([
              styles.contentContainer,
              { paddingBottom: bottom },
              contentContainerStyle,
            ])}
          >
            {children}
          </BottomSheetView>
        )}
      </BottomSheetModal>
    );
  },
);

const styles = StyleSheet.create({
  backgroundStyle: {
    borderRadius: 8,
  },
  contentContainer: {
    backgroundColor: theme.color.background.primary,
    paddingBottom: isIOS ? theme.spacing.xl : 0,
    paddingTop: theme.spacing.m,
  },
  handleStyle: {
    padding: 0,
  },
  indicatorStyle: {
    backgroundColor: theme.color.text.muted,
    height: 6,
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.m,
    paddingHorizontal: 0,
    width: 36,
  },
  modalStyle: {
    elevation: 9999,
    zIndex: 9999,
  },
  webContainer: {
    marginHorizontal: 'auto',
    maxWidth: SMALL_SCREEN_MAX_WIDTH,
  },
});

export default BottomSheet;
